"use client"

import { useState, useEffect } from "react"
import { useSuggestedTagsStore } from "@/lib/suggested-tags-store"
import { useUserReputationStore } from "@/lib/user-reputation-store"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { ScrollArea } from "@/components/ui/scroll-area"
import { ThumbsUp, ThumbsDown, Plus } from "@/lib/icons"
import { toast } from "sonner"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import type React from "react"
import { useTranslations } from "next-intl"

type ArtistCategory = "artist" | "va" | "sound_designer"

interface SuggestedArtistsPanelProps {
  contentId: string
  onArtistAdd?: (artist: string) => void
  availableTags?: string[]
}

function getStableUserId(): string {
  if (typeof window === "undefined") return "server-user"
  let id = localStorage.getItem("goonru-user-id")
  if (!id) {
    id = `user-${Math.random().toString(36).substr(2, 9)}-${Date.now()}`
    localStorage.setItem("goonru-user-id", id)
  }
  return id
}

export function SuggestedArtistsPanel({ contentId, onArtistAdd, availableTags = [] }: SuggestedArtistsPanelProps) {
  const tt = useTranslations("suggestedArtistsPanel")
  const { getTagsForContent, upvoteTag, downvoteTag, removeUpvote, removeDownvote } = useSuggestedTagsStore()
  const { recordVote, removeVote, getVoteType, isMaxRepUser } = useUserReputationStore()
  const [newArtist, setNewArtist] = useState("")
  const [userId, setUserId] = useState("temp-user")
  const [showAutocomplete, setShowAutocomplete] = useState(false)
  const [filteredArtists, setFilteredArtists] = useState<string[]>([])
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [selectedCategory, setSelectedCategory] = useState<ArtistCategory>("artist")

  useEffect(() => {
    setUserId(getStableUserId())
  }, [])

  useEffect(() => {
    if (newArtist.trim().length >= 2) {
      const searchTerm = newArtist.toLowerCase()
      const matches = availableTags
        .filter((tag) => {
          const lower = tag.toLowerCase()
          return (
            lower.includes(searchTerm) &&
            (lower.includes("artist") || lower.includes("_(artist)") || lower.startsWith("artist:"))
          )
        })
        .slice(0, 10)
      setFilteredArtists(matches)
      setShowAutocomplete(matches.length > 0)
      setSelectedIndex(0)
    } else {
      setShowAutocomplete(false)
      setFilteredArtists([])
    }
  }, [newArtist, availableTags])

  const artistContentId = `artist:${contentId}`
  const suggestedArtists = getTagsForContent(artistContentId)
  const isMaxRep = isMaxRepUser(userId)

  const selectArtistFromAutocomplete = (artist: string) => {
    setNewArtist(artist)
    setShowAutocomplete(false)
    setFilteredArtists([])
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showAutocomplete) {
      if (e.key === "Enter") {
        handleSuggestArtist()
      }
      return
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault()
        setSelectedIndex((prev) => (prev < filteredArtists.length - 1 ? prev + 1 : prev))
        break
      case "ArrowUp":
        e.preventDefault()
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev))
        break
      case "Enter":
        e.preventDefault()
        if (filteredArtists[selectedIndex]) {
          selectArtistFromAutocomplete(filteredArtists[selectedIndex])
        } else {
          handleSuggestArtist()
        }
        break
      case "Escape":
        setShowAutocomplete(false)
        break
    }
  }

  const handleSuggestArtist = () => {
    if (!newArtist.trim()) return

    const normalizedInput = newArtist.trim().toLowerCase().replace(/\s+/g, "_")
    const existingArtist = availableTags.find((tag) => {
      const normalizedTag = tag.toLowerCase()
      return (
        normalizedTag === normalizedInput ||
        normalizedTag === `${normalizedInput}_(artist)` ||
        normalizedTag.replace("artist:", "") === normalizedInput ||
        normalizedTag.includes(normalizedInput)
      )
    })

    const { suggestTag } = useSuggestedTagsStore.getState()
    const artistTag = formatArtistWithCategory(newArtist.trim(), selectedCategory)

    if (existingArtist) {
      suggestTag(artistTag, artistContentId, userId, 4)
      setNewArtist("")
      setShowAutocomplete(false)
      toast.success(tt("artistExistingArtistAddedToPendingWith", { existingArtist: existingArtist }))
      return
    }

    suggestTag(artistTag, artistContentId, userId, 0)
    setNewArtist("")
    setShowAutocomplete(false)
    setSelectedCategory("artist")
    toast.success(tt("artistSuggestionAddedNeedsCommunityApprova"))
  }

  const handleUpvote = (tagId: string, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    const currentVote = getVoteType(userId, tagId)

    if (currentVote === "upvote") {
      removeVote(userId, tagId)
      removeUpvote(tagId)
      toast.success(tt("upvoteRemoved"))
    } else if (currentVote === "downvote") {
      removeDownvote(tagId)
      upvoteTag(tagId, userId)
      recordVote(userId, tagId, "upvote")
      toast.success(tt("changedToUpvote"))
    } else {
      recordVote(userId, tagId, "upvote")
      upvoteTag(tagId, userId)
      toast.success(tt("upvoted"))
    }
  }

  const handleDownvote = (tagId: string, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    const currentVote = getVoteType(userId, tagId)

    if (currentVote === "downvote") {
      removeVote(userId, tagId)
      removeDownvote(tagId)
      toast.success(tt("downvoteRemoved"))
    } else if (currentVote === "upvote") {
      removeUpvote(tagId)
      downvoteTag(tagId, userId)
      recordVote(userId, tagId, "downvote")
      toast.success(tt("changedToDownvote"))
    } else {
      recordVote(userId, tagId, "downvote")
      downvoteTag(tagId, userId)
      toast.success(tt("downvoted"))
    }
  }

  const formatArtistWithCategory = (name: string, category: ArtistCategory): string => {
    if (category === "artist") return `${name}_(artist)`
    if (category === "va") return `voiced_by_${name}`
    if (category === "sound_designer") return `sound_design_by_${name}`
    return name
  }

  const approvedArtists = suggestedArtists.filter((t) => t.isApproved)
  const pendingArtists = suggestedArtists.filter((t) => !t.isApproved)

  return (
    <div className="space-y-4 px-2">
      <div className="space-y-2">
        <label className="text-sm font-medium mb-0 pb-0">{tt("suggestAnArtist")}</label>
        <div className="flex gap-2 mt-3">
          <div className="relative flex-1">
            <Input
              placeholder={tt("enterArtistName")}
              value={newArtist}
              onChange={(e) => setNewArtist(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={() => setTimeout(() => setShowAutocomplete(false), 200)}
              onFocus={() => {
                if (filteredArtists.length > 0) setShowAutocomplete(true)
              }}
              className="h-8 mt-0"
            />
            {showAutocomplete && filteredArtists.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-lg max-h-60 overflow-y-auto">
                {filteredArtists.map((artist, index) => (
                  <button
                    key={artist}
                    type="button"
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-accent/50 transition-colors ${
                      index === selectedIndex ? "bg-accent" : ""
                    }`}
                    onClick={() => selectArtistFromAutocomplete(artist)}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    <span className="font-medium">{artist.replace(/_/g, " ")}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <Select value={selectedCategory} onValueChange={(value) => setSelectedCategory(value as ArtistCategory)}>
            <SelectTrigger className="h-8 w-44 mt-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="artist">{tt("artist")}</SelectItem>
              <SelectItem value="va">{tt("voiceActor")}</SelectItem>
              <SelectItem value="sound_designer">{tt("soundDesigner")}</SelectItem>
            </SelectContent>
          </Select>
          <Button className="mt-0" onClick={handleSuggestArtist} size="sm">
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        <div className="mt-2 flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{tt("preview")}</span>
          {newArtist && (
            <span className="text-xs font-medium text-purple-600">
              {formatArtistWithCategory(newArtist, selectedCategory)}
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground mb-2.5 py-0.5 pb-1">
          {tt("suggestAnArtistTagOrLinkAnExistingOneR")}
        </p>
      </div>

      {approvedArtists.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-purple-600">{tt("approvedArtists")}</h4>
          <div className="flex flex-wrap gap-2">
            {approvedArtists.map((artist) => {
              const userVote = getVoteType(userId, artist.id)
              return (
                <div key={artist.id} className="flex items-center gap-1">
                  <Badge
                    className="bg-purple-500/20 text-purple-700 hover:bg-purple-500/30 cursor-pointer"
                    onClick={() => onArtistAdd?.(artist.tag)}
                  >
                    {artist.tag.replace(/_/g, " ").replace("(artist)", "")}
                  </Badge>
                  <span className="text-xs text-muted-foreground">{artist.upvotes}</span>
                  <Button
                    size="sm"
                    variant={userVote === "downvote" ? "default" : "ghost"}
                    className={`h-6 w-6 p-0 ${userVote === "downvote" ? "bg-primary text-primary-foreground" : ""}`}
                    onClick={(e) => handleDownvote(artist.id, e)}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <ThumbsDown className="h-3 w-3" />
                  </Button>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {pendingArtists.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold">{tt("pendingArtistSuggestions")}</h4>
          <ScrollArea className="h-48">
            <div className="space-y-2 pr-[]">
              {pendingArtists.map((artist) => {
                const userVote = getVoteType(userId, artist.id)
                return (
                  <div key={artist.id} className="flex items-center justify-between p-2 rounded-md bg-secondary/30">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{artist.tag.replace(/_/g, " ").replace("(artist)", "")}</Badge>
                      <div className="flex gap-3 text-xs text-muted-foreground">
                        <span className="text-green-600">{tt("upvotes6Upvote", { upvotes: artist.upvotes })}</span>
                        <span className="text-red-600">{tt("downvotes6Downvote", { downvotes: artist.downvotes })}</span>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant={userVote === "upvote" ? "default" : "ghost"}
                        className={`h-6 w-6 p-0 ${userVote === "upvote" ? "bg-primary text-primary-foreground" : ""}`}
                        onClick={(e) => handleUpvote(artist.id, e)}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <ThumbsUp className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant={userVote === "downvote" ? "default" : "ghost"}
                        className={`h-6 w-6 p-0 ${userVote === "downvote" ? "bg-primary text-primary-foreground" : ""}`}
                        onClick={(e) => handleDownvote(artist.id, e)}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <ThumbsDown className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                )
              })}
            </div>
          </ScrollArea>
        </div>
      )}

      {suggestedArtists.length === 0 && (
        <div className="text-center py-4 text-muted-foreground text-sm">
          {tt("noSuggestedArtistsYetBeTheFirstToSugges")}
        </div>
      )}
    </div>
  )
}
